import Baobab from "baobab";
import env from "../constants/env";

import contentEN_US from "../content/en-US/content.js";

import { getLs, getFirstLevelItemType, getQueryStringParam, isDev } from "../utils/utils";

const { appCapabilities } = env;

const state = new Baobab(
	{
		removeAllLogs: isDev() ? false : true,
		lang: "en-US",
		version: {
			copyright: "© 2017-2026 AmbiFi, Inc. - All Rights Reserved",
			prefix: "Version",
			major: "10",
			minor: "164",
			revision: "0",
			build: "4643",
			suffix: ""
		},
		appCapabilities,
		device: null,
		devices: [],
		activities: [],
		optimisticInstanceMap: {},
		checklistHistory: null,
		tree: {},
		editActivityMetaData: {
			inlineVariables: {}
		},
		selectedNodePath: [],
		typeahead: [],
		clipboardPasteText: "",
		clipboard: {},
		clipboardExternalResources: [],
		clipboardRelatedItems: [],
		clipboardMode: "",
		history: [],
		historyIndex: -1,
		linkActionTypes: [
			{
				label: "Goto",
				action: "goto",
				description: "Will navigate to the target, this action does not provide a back button.",
				nav: true
			},
			{
				label: "Goto (New Session)",
				action: "newSessionAndGoto",
				description: "Will navigate to the target and start a new session, meaning the activity will start from the beginning and no data will be filled in, this action does not provide a back button.",
				nav: true
			},
			{
				label: "Goto (Reset Items In Range)",
				action: "resetRangeAndGoto",
				description: "Will navigate to the target and reset all items between (and including) the current item and the target, this action does not provide a back button.",
				nav: true
			},
			{
				label: "Goto (Check Items In Range)",
				action: "checkRangeAndGoto",
				description: "Will navigate to the target and check all items between (and including) the current item and the target, this action does not provide a back button.",
				nav: true
			},
			{
				label: "Goto and Return",
				action: "gotoAndReturn",
				description: "Will navigate to the target, this action provides the user with a back button and allows them to navigate back to where they came from.",
				nav: true
			},
			{
				label: "Goto and Return (New Session)",
				action: "newSessionAndGotoAndReturn",
				description: "Will navigate to the target and start a new session, meaning the activity will start from the beginning and no data will be filled in, this action provides the user with a back button and allows them to navigate back to where they came from.",
				nav: true
			},
			{
				label: "Goto and Open Birdseye View",
				action: "gotoAndOpenBirdseyeView",
				description: "Will navigate to the target and open the Birdseye View. The target ID should be set on a List.",
				nav: true
			},
			{
				label: "Show Item in Dialog",
				description: "This will allow to user to remain at the current location but show a dialog with the content of the specified item, this dialog can then be dismissed by the user at which point they pick up where they left off.",
				action: "showModalItem",
				nav: true,
				internalOnly: true
			},
			// {
			// 	label: "Show Informational Content in Dialog",
			// 	description: "This will you to add labels and media to show to a user without having to create a separate item.",
			// 	action: "showModalDynamicItem"
			// },
			{
				label: "Go Back",
				description: "Will perform a navigate back action if it is available (e.g.: after a go to and return).",
				action: "goBack"
			},
			{
				label: "Save + Exit",
				description: "Saves the current Activity instance and navigates back to the home screen.",
				action: "saveActivityAndExit"
			},
			{
				label: "Reset Current Activity",
				description: "Will erase all data in the current activity and take the user to the beginning of the activity.",
				action: "resetCurrentActivity"
			},
			{
				label: "Reset Current List",
				description: "Will erase all data in the current list and take the user to the beginning of the list.",
				action: "resetCurrentList"
			},
			{
				label: "Reset Current Section",
				description: "Will erase all data in the current section and take the user to the beginning of the section.",
				action: "resetCurrentSection"
			},
			{
				label: "Repeat Current List",
				description: "Used in conjuction with the repeatble property, creates a new List based on the current List.",
				action: "repeatCurrentList"
			},
			{
				label: "Repeat Current Section",
				description: "Used in conjunction with the repeatable property, creates a new section based on the current Section.",
				action: "repeatCurrentSection"
			},
			{
				label: "Repeat Current Item",
				description: "Used in conjunction with the repeatable property, creates a new item based on the current Item.",
				action: "repeatCurrentItem"
			},
			{
				label: "Open Comments",
				description: "Will open author comments.",
				action: "openComments"
			},
			{
				label: "Open Notes",
				description: "Will open the notes to allow a user to capture additional information.",
				action: "openNotes"
			},
			{
				label: "Open Media",
				description: "Will open the media attachment dialog that allows users to add images and videos as supplemental notes.",
				action: "openMedia"
			},
			{
				label: "Open Sketchpad",
				description: "Will open the sketchpad dialog that allows users to add drawn sketches as supplemental notes.",
				action: "openSketchpad"
			},
			{
				label: "External",
				description: "An external URL that will be opened in the browser.",
				action: "external"
			}
		],
		itemTypes: [
			{
				name: "Check Item",
				type: "item",
				icon: "check"
			},
			{
				name: "Check Item (Label Only)",
				type: "itemLabelOnly",
				icon: "spell-check"
			},
			{
				name: "Text Input",
				type: "itemTextInput",
				icon: "font"
			},
			{
				name: "Text Input (Multiline)",
				type: "itemTextInputMultiline",
				icon: "font"
			},
			{
				name: "Yes / No",
				type: "itemYesNo",
				icon: "thumbs-up"
			},
			{
				name: "Picker",
				type: "itemPicker",
				icon: "list"
			},
			{
				name: "Single Choice",
				type: "itemChoiceSingle",
				icon: ["far", "dot-circle"]
			},
			{
				name: "Multi Choice",
				type: "itemChoiceMulti",
				icon: ["far", "check-square"]
			},
			{
				name: "Ranking",
				type: "itemRanking",
				icon: "sort"
			},
			{
				name: "Date Picker",
				type: "itemDate",
				icon: "calendar"
			},
			{
				name: "Time Picker",
				type: "itemTime",
				icon: "calendar"
			},
			{
				name: "Date Time Picker",
				type: "itemDateTime",
				icon: "calendar"
			},
			{
				name: "Weather",
				type: "itemWeather",
				icon: "cloud"
			},
			{
				name: "Address Search",
				type: "itemAddress",
				icon: "map-marked-alt"
			},
			{
				name: "Location Search",
				type: "itemLocation",
				icon: "location-arrow"
			},
			{
				name: "Media Upload",
				type: "itemImagePicker",
				icon: ["far", "file-image"]
			},
			{
				name: "Barcode Scanner",
				type: "itemBarcodeScanner",
				icon: "barcode"
			},
			{
				name: "Sketch Pad",
				type: "itemSketchPad",
				icon: "edit"
			}
		],
		genres: [
			{
				value: "auto",
				name: "Automobile",
				icon: "car",
				iconPng: "https://i.imgur.com/qyUEp4f.png"
			},
			{
				value: "aviation",
				name: "Aviation",
				icon: "plane",
				iconPng: "https://i.imgur.com/WHjyNob.png"
			},
			{
				value: "drone",
				name: "Drone",
				icon: "fighter-jet",
				iconPng: "https://i.imgur.com/WzXw8Po.png"
			},
			{
				value: "bike",
				name: "Bike",
				icon: "bicycle",
				iconPng: "https://i.imgur.com/0AMfTYN.png"
			},
			{
				value: "boat",
				name: "Boat",
				icon: "ship",
				iconPng: "https://i.imgur.com/KtcTbZj.png"
			},
			{
				value: "fun",
				name: "Fun",
				icon: "puzzle-piece",
				iconPng: "https://i.imgur.com/RIp6GG1.png"
			},
			{
				value: "food",
				name: "Food",
				icon: "utensil-spoon",
				iconPng: "https://i.imgur.com/FQd4TWZ.png"
			},
			{
				value: "games",
				name: "Games",
				icon: "gamepad",
				iconPng: "https://i.imgur.com/quGKE9k.png"
			},
			{
				value: "groceries",
				name: "Groceries",
				icon: "shopping-cart",
				iconPng: "https://i.imgur.com/nN6A66S.png"
			},
			{
				value: "health",
				name: "Health",
				icon: "ambulance",
				iconPng: "https://i.imgur.com/mMIK26a.png"
			},
			{
				value: "holidays",
				name: "Holidays",
				icon: "tree",
				iconPng: "https://i.imgur.com/3lp6LCd.png"
			},
			{
				value: "home",
				name: "Home",
				icon: "home",
				iconPng: "https://i.imgur.com/HPiip9M.png"
			},
			{
				value: "kids",
				name: "Kids",
				icon: "smile",
				iconPng: "https://i.imgur.com/VPstdR1.png"
			},
			{
				value: "legal",
				name: "Legal",
				icon: "gavel",
				iconPng: "https://i.imgur.com/DHyXMgi.png"
			},
			{
				value: "learning",
				name: "Learning",
				icon: "book",
				iconPng: "https://i.imgur.com/itjRDLW.png"
			},
			{
				value: "maintenance",
				name: "Maintenance",
				icon: "wrench",
				iconPng: "https://i.imgur.com/FxgGsnk.png"
			},
			{
				value: "money",
				name: "Money",
				icon: "money-bill-alt",
				iconPng: "https://i.imgur.com/Tyz2Fpu.png"
			},
			{
				value: "movies",
				name: "Movies",
				icon: "ticket",
				iconPng: "https://i.imgur.com/7Kk82Wj.png"
			},
			{
				value: "music",
				name: "Music",
				icon: "music",
				iconPng: "https://i.imgur.com/KiDWWwM.png"
			},
			{
				value: "motorcycle",
				name: "Motorcycle",
				icon: "motorcycle",
				iconPng: "https://i.imgur.com/1bfmjEY.png"
			},
			{
				value: "people",
				name: "People",
				icon: "users",
				iconPng: "https://i.imgur.com/VU2dTse.png"
			},
			{
				value: "pets",
				name: "Pets",
				icon: "paw",
				iconPng: "https://i.imgur.com/niAuSzv.png"
			},
			{
				value: "photo",
				name: "Photography",
				icon: "camera-retro",
				iconPng: "https://i.imgur.com/a4VTwWx.png"
			},
			{
				value: "restaurants",
				name: "Restaurants",
				icon: "utensils",
				iconPng: "https://i.imgur.com/KWJl1lu.png"
			},
			{
				value: "school",
				name: "School",
				icon: "graduation-cap",
				iconPng: "https://i.imgur.com/FE7UPUo.png"
			},
			{
				value: "science",
				name: "Science",
				icon: "flask",
				iconPng: "https://i.imgur.com/i4nnHbD.png"
			},
			{
				value: "shopping",
				name: "Shopping",
				icon: "shopping-bag",
				iconPng: "https://i.imgur.com/McFzyKa.png"
			},
			{
				value: "social",
				name: "Social",
				icon: "beer",
				iconPng: "https://i.imgur.com/ZO5Js7h.png"
			},
			{
				value: "sports",
				name: "Sports",
				icon: "futbol",
				iconPng: "https://i.imgur.com/IQAAL43.png"
			},
			{
				value: "tv",
				name: "Television",
				icon: "tv",
				iconPng: "https://i.imgur.com/RVEgTL6.png"
			},
			{
				value: "travel",
				name: "Travel",
				icon: "hotel",
				iconPng: "https://i.imgur.com/zaxDHNy.png"
			},
			{
				value: "video",
				name: "Video",
				icon: "video",
				iconPng: "https://i.imgur.com/m3mRYo9.png"
			},
			{
				value: "work",
				name: "Work",
				icon: "chart-bar",
				iconPng: "https://i.imgur.com/vBP2oLl.png"
			},
			{
				value: "misc",
				name: "Miscellaneous",
				icon: "question",
				iconPng: "https://i.imgur.com/lVSsmXj.png"
			}
		],
		tagSuggestions: [
		],
		groupNames: [
		],
		filterTags: [
		],
		user: {
			sub: "",
			name: "",
			username: "",
			email: "",
			password: "",
			code: "",
			identityId: "",
			loggedIn: false,
			historyUsers: [],
			entryPoint: "",
			org: null,
			savePassword: true,
			rememberMe: true,
			sharedDevice: false
		},
		timerTemplates: [],
		templates: {
			customTimer: {
				"id": "",
				"name": "",
				"type": "countdown",
				"mode": "normal",
				"viewType": "",
				"label1": "",
				"label2": "",
				"hours": 0,
				"minutes": 0,
				"seconds": 0,
				"hideDismiss": false,
				"suppressAudio": false,
				"action": {
					"type": "message",
					"message": "",
					"action": "",
					"linkItemId": "",
					"checklistId": ""
				},
				"triggers": [
				]
			}
		},
		appState: {
			showGuids: false,
			priceListMap: null,
			customerMap: null,
			aiAssistantResponse: "",
			aiAssistantResponseLast: "",
			aiAssistantLaunchRequest: null,
			aiAssistantFocusedTarget: null,
			forceEditorRefresh: 0,
			hideHomeAlert: false,
			preview: {
				previewScale: "100",
				previewWidth: "100%",
				previewHeight: "100%",
				resolution: "responsive",
				resolutionDisabled: false
			},
			errorSpinner: {
				show: false
			},
			templateModal: {
				show: false
			},
			visibleIfSort: "outline",
			visibleIfShowIds: true,
			idGeneratorModal: {
				show: false,
				defaults: null
			},
			reasignModal: {
				show: false
			},
			playlistsModal: {
				show: false
			},
			startupModal: {
				showModal: true
			},
			timerModal: {
				show: false,
				timerTemplate: {}
			},
			mediaPreviewModal: {
				show: false,
				url: "",
				propName: ""
			},
			checklistReceived: false,
			checklistReceivedMessage: "Activity successfully received!",
			lastIdentityId: "",
			lastChecklistId: "",
			showDialogPriceTiers: false,
			registrationStep: 1,
			loading: false,
			loadingMessage: "",
			nativeHcaSsoRedirectPending: false,
			notifications: {
				visible: false,
				loading: false,
				refreshing: false,
				error: "",
				items: [],
				unreadCount: 0,
				lastLoadedAt: ""
			},
			nodes: {

			},
			alerts: [],
			confirm: {
				message: "",
				title: "",
				onConfirm: null
			},
			documents: {
				initialSpinnerShow: false,
				showSpinner: false,
				spinnerMessage: "",
				brokenInventoryEntries: {},
				removedInventoryEntryIds: {}
			},
			editor: {
				multiSelectMode: false,
				showSpinner: false,
				spinnerMessage: ""
			},
			navPanel: {
				selected: "home"
			},
			previewChecklist: {
				checklist: {},
				showModal: false,
				showInline: false,
				updateMode: "manual",
				live: false,
				stateUpdatesPending: false,
				allVisible: true,
				activityVars: {},
				activityVarsProfileKey: "",
				drive: {}
			},
			showWorkflow: {
				checklist: {},
				showModal: false,
				url: ""
			},
			showWorkflowForMultiple: {
				checklist: {},
				showModal: false
			},
			showPricing: {
				checklist: {},
				pricingSnapshotId: "base",
				pricingSnapshotIdTemp: "base",
				pricingMode: "wholesale",
				pricingModeTemp: "wholesale",
				selectedValue: "",
				selectedValueTemp: "",
				invoiceItems: [],
				invoiceItemsTemp: [],
				estimates: [],
				estimatesTemp: [],
				jobDetails: {
					name: "",
					description: "",
					instructions: "",
					notes: "",
					parts: ""
				},
				jobDetailsTemp: {
					name: "",
					description: "",
					instructions: "",
					notes: "",
					parts: ""
				},
				pricingContractProto: null,
				pricingContractProtoTemp: null,
				projectOpsContext: null,
				source: {},
				sourceContext: null,
				showModal: false,
				url: ""
			},
			showEstimateManager: {
				checklist: {},
				hit: {},
				initialEstimateValue: "",
				loading: false,
				saving: false,
				showModal: false,
				viewerMode: "manage",
				url: ""
			},
			showNotes: {
				checklist: {},
				showModal: false,
				sessionName: ""
			},
			showDetailedStatus: {
				checklist: {},
				checklistUserId: "",
				showModal: false,
				edit: false,
				modal: {
					showSpinner: false
				}
			},
			showDetailedHistory: {
				checklist: {},
				showModal: false
			},
			showCreateChatChannel: {
				activeChannelId: null,
				activeChannelCid: null,
				instance: {},
				openPinsOnLoad: false,
				showModal: false,
				isEditing: false
			},
			streamChat: {
				chatUnreadMessagesCount: null,
				statusUnreadMessagesCount: null,
				navigationFallbackUnreadMessagesCount: null,
				jobUnreadMessagesCount: null,
				jobUnreadByInstanceId: null,
				unreadMessagesCount: 0,
				unreadChannelsCount: 0
			},
			lastLocation: null,
			filters: {
				showVisible: true,
				showDependencies: true,
				all: {
					genres: [],
					tags: []
				},
				settings: {
					expandGenres: false,
					expandTags: false,
					filtersCollapsed: true,
					sortGenresByCount: false,
					sortTagsByCount: false
				}
			},
			spinners: {
				orgs: {
					showMembersPanelSpinner: false
				}
			},
			assignments: {
				showLoader: false,
				showCreateModal: false,
				showEditModal: false
			},
			groupManagement: {
				showCreateEditModal: false,
				showCreateUpdateSpinner: false
			},
			scheduleManagement: {
				showCreateEditModal: false,
				showCreateUpdateSpinner: false
			},
			reporting: {
				showPdfPreviewLoader: false
			},
			cms: {
				models: [],
				flattenedModel: {}
			},
			selectedActivities: [],
			selectedMembers: [],
			selectedSearchItems: [],
			selectedStatusItems: [],
			searchItems: [],
			statusItems: [],
			statusViewType: "card-view",
			relatedItems: {
				clipboard: ""
			},
			urlParams: {},
			demoRole: "",
			enterprise: false,
			subDomain: "",
			tabChat: "customer"
		},
		orgs: [],
		selectedOrg: {
			groups: [
				{
					id: "57a08a11-5196-459b-a800-80aa38b5eb58",
					name: "Managers",
					logoUrl: "",
					members: [
						{
							sub: "cf7a3d28-f120-49a8-b4f6-e73375919cfa",
							username: "alexa.poc",
							displayName: "AmbiFi Home",
							profileImageUrl: ""
						},
						{
							sub: "b9269679-7b25-4ce1-983b-16085e28c9d2",
							username: "maintenance.poc",
							displayName: "Maintenance Home",
							profileImageUrl: ""
						}
					]
				}
			],
			members: [
				{
					sub: "cf7a3d28-f120-49a8-b4f6-e73375919cfa",
					username: "alexa.poc",
					displayName: "AmbiFi Home",
					profileImageUrl: ""
				},
				{
					sub: "b9269679-7b25-4ce1-983b-16085e28c9d2",
					username: "maintenance.poc",
					displayName: "Maintenance Home",
					profileImageUrl: ""
				},
				{
					sub: "5f872edf-7783-47ab-9201-836a166ef3a3",
					username: "testuser1",
					displayName: "Test User1",
					profileImageUrl: ""
				}
			]
		},
		signup: {
			email: "",
			name: "",
			displayName: null,
			orgName: "",
			orgDomain: "",
			step: 1,
			showSpinner: false
		},
		search: {
			featured: [],
			checkmate: [],
			trending: [],
			recommended: []
		},
		import: {
			showSpinner: false
		},
		inviteMember: {
			showSpinner: false,
			inviteCode: null,
			role: 0 // Default to User
		},
		groupManagement: {},
		scheduleManagement: {},
		recent: [],
		recentActivityCache: {},
		editOrg: {},
		connection: {
			internetConnection: null,
			networkType: null
		},
		player: {
			checklist: {},
			checklistIdentityId: "",
			activities: [],
			activitiesLoaded: false,
			sessions: {},
			sessionsMap: {},
			sortedSesions: [],
			showSpinner: false,
			selectedActivityId: "",
			selectedActivityInstanceId: "",
			showSessionsModal: false,
			globalVars: {},
			navStack: [],
			reloadActivities: false,
			selectedHomeTab: "myActivities",
			showComments: false,
			showCommentsIdx: 0,
			statusDetailSessions: {}
		},
		drive: {
			settings: {
				mode: getLs("driveUi") ? getLs("driveUi") : "list"
			},
			children: null,
			modals: {
				saveModal: {
					show: false,
					content: null,
					path: null,
					fileName: null
				}
			},
			uploads: {},
			hierarchyMetadata: {},
			selection: {
				items: []
			},
			selectedFolder: ""
		},
		assignments: {
			showSpinner: false,
			list: [],
			selected: {},
			cronPresets: [
				{
					label: "Monday thru Friday @ 1pm",
					value: "0 0 13 ? * 1-5 *"
				},
				{
					label: "Every Monday in Februrary @ 1pm",
					value: "0 0 13 ? FEB MON *"
				},
				{
					label: "Every first Monday of the month @ 1pm",
					value: "0 0 13 ? 1/1 MON#1 *"
				}
			],
			schedule: []
		},
		generalClipboard: null,
		queryString: {},
		functions: "",
		publish: {
			status: {},
			newPublishConfig: {

			},
			showConfigModal: false,
			showSpinner: false,
			showPageSpinner: false,
			publishCompleteModal: {
				show: false,
				url: "",
				showDownloadButton: false,
				showClipboardButton: false
			}
		},
		playlists: [],
		playlistId: "",
		exportToZip: {
			status: {},
			config: {

			},
			showConfigModal: false,
			showSpinner: false,
			showPageSpinner: false
		},
		itemPreferences: {
			itemHeightOptions: [{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			itemHeightHierarchyOptions: [{
				label: "Inherit",
				value: "inherit"
			},
			{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			imageMaxHeightOptions: [{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			imageMaxHeightHierarchyOptions: [{
				label: "Inherit",
				value: "inherit"
			},
			{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			imageKenBurnsEffectDuration: [{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			imageKenBurnsEffectStartScaleX: [{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			imageKenBurnsEffectStartScaleY: [{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			imageKenBurnsEffectEndScaleX: [{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			imageKenBurnsEffectEndScaleY: [{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			videoMaxHeightOptions: [{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}],
			videoMaxHeightHierarchyOptions: [{
				label: "Inherit",
				value: "inherit"
			},
			{
				label: "Default",
				value: "default"
			},
			{
				label: "Custom",
				value: "custom"
			}]
		},
		activityPreferences: {
			activityDefaultColumnWidthOptions: [
				{
					label: "User Preference",
					value: "userPreference"
				},
				{
					label: "Custom",
					value: "custom"
				}
			],
			activityChildDefaultColumnWidthOptions: [
				{
					label: "Inherit",
					value: "inherit"
				},
				{
					label: "User Preference",
					value: "userPreference"
				},
				{
					label: "Custom",
					value: "custom"
				}
			],
			activityChildBackgroundAudioDuckingOptions: [
				{
					label: "Inherit",
					value: "inherit"
				},
				{
					label: "Duck",
					value: true
				},
				{
					label: "Don't duck",
					value: false
				}
			],
			activityChildBackgroundAudioDurationVolumeOptions: [
				{
					label: "Inherit",
					value: "inherit"
				},
				{
					label: "Custom",
					value: "custom"
				}
			]
		},
		printTemplates: [],
		audio: {
			modals: {
				generateTts: {
					show: false
				},
				generateTtsForProperty: {
					show: false,
					propName: "",
					previewUrl: "",
					previewText: ""
				}
			},
			generateTtsConfig: {
				voiceId: "",
				sampleRate: null,
				engine: ""
			}
		},
		filters: {},
		content: contentEN_US,
		contentAll: {
			"en-US": contentEN_US
		},
		editor: {
			previewVisible: true,
			previewPopoutOpen: false,
			sourceDocumentsVisible: false,
			sourceDocumentsPopoutOpen: false,
			separateOutlineAndProps: true,
			layouts: [
				{
					name: "Outline / Properties / Preview",
					value: "outline-props-preview",
					disabled: false
				},
				{
					name: "Combined Outline + Properties / Preview",
					value: "outline-and-preview",
					disabled: false
				},
				{
					name: "Outline / Properties / Source Documents",
					value: "outline-props-sources",
					disabled: false
				},
				{
					name: "Combined Outline + Properties / Source Documents",
					value: "outline-and-sources",
					disabled: false
				},
				{
					name: "Outline / Properties / NO Preview",
					value: "outline-props-no-preview",
					disabled: false
				},
				{
					name: "Combined Outline + Properties / NO Preview",
					value: "outline-no-preview",
					disabled: false
				}
			]
		}
	},
	{
		immutable: false
	}
);

// Resolve these initially, could use monkey as well
state.set(["appState", "editor", "previewVisible"], state.get(["editor", "previewVisible"]));
state.set(["appState", "editor", "previewPopoutOpen"], state.get(["editor", "previewPopoutOpen"]));
state.set(["appState", "editor", "sourceDocumentsVisible"], state.get(["editor", "sourceDocumentsVisible"]));
state.set(["appState", "editor", "sourceDocumentsPopoutOpen"], state.get(["editor", "sourceDocumentsPopoutOpen"]));
state.set(["appState", "editor", "separateOutlineAndProps"], state.get(["editor", "separateOutlineAndProps"]));

function isStateUpdateDebugEnabled() {
	if (!isDev()) {
		return false;
	}

	const debugParam = getQueryStringParam("stateDebug");
	return debugParam === true || debugParam === "true" || debugParam === 1 || debugParam === "1";
}

const LIVE_PREVIEW_TEXT_UPDATE_DELAY_MS = 400;
const PREVIEW_PLAYER_SELECTION_SYNC_SUPPRESSION_MS = 1000;
export const PREVIEW_UPDATE_MODE_MANUAL = "manual";
export const PREVIEW_UPDATE_MODE_ON_REFRESH = "onRefresh";
export const PREVIEW_UPDATE_MODE_LIVE = "live";
const LIVE_PREVIEW_TEXT_UPDATE_FIELDS = new Set([
	"comments",
	"commentsAuthor",
	"commentsPrint",
	"commentsTemplate",
	"label",
	"label1",
	"label1AlexaPlain",
	"label1AlexaRich",
	"label1Print",
	"label2",
	"label2AlexaPlain",
	"label2AlexaRich",
	"label2Print",
	"name",
	"nameAlexaPlain",
	"nameAlexaRich",
	"namePrint",
	"subtitle",
	"title"
]);
let pendingLivePreviewStateUpdates = [];
let pendingLivePreviewStateUpdateTimer = null;

export function getPreviewUpdateMode() {
	const mode = state.get(["appState", "previewChecklist", "updateMode"]);
	if (mode === PREVIEW_UPDATE_MODE_ON_REFRESH || mode === PREVIEW_UPDATE_MODE_LIVE || mode === PREVIEW_UPDATE_MODE_MANUAL) {
		return mode;
	}

	return state.get(["appState", "previewChecklist", "live"]) ? PREVIEW_UPDATE_MODE_LIVE : PREVIEW_UPDATE_MODE_MANUAL;
}

function isDebounceableLivePreviewStateUpdate(stateUpdate) {
	const path = Array.isArray(stateUpdate && stateUpdate.path) ? stateUpdate.path : [];
	const propertyName = path.length > 0 ? path[path.length - 1] : "";
	return stateUpdate &&
		stateUpdate.transactionType === "set" &&
		stateUpdate.type === "entities" &&
		LIVE_PREVIEW_TEXT_UPDATE_FIELDS.has(propertyName);
}

function getLivePreviewStateUpdateKey(stateUpdate = {}) {
	return JSON.stringify({
		path: stateUpdate.path || [],
		transactionType: stateUpdate.transactionType || "",
		type: stateUpdate.type || ""
	});
}

function isSelectedStateUpdate(stateUpdate) {
	const path = Array.isArray(stateUpdate && stateUpdate.path) ? stateUpdate.path : [];
	return stateUpdate &&
		stateUpdate.type === "entities" &&
		stateUpdate.transactionType === "set" &&
		path[path.length - 1] === "selected" &&
		stateUpdate.value === true;
}

export function clearPendingPreviewStateUpdates() {
	if (pendingLivePreviewStateUpdateTimer) {
		clearTimeout(pendingLivePreviewStateUpdateTimer);
		pendingLivePreviewStateUpdateTimer = null;
	}
	pendingLivePreviewStateUpdates = [];
	state.set(["appState", "previewChecklist", "stateUpdatesPending"], false);
}

export function flushPendingPreviewStateUpdates() {
	if (pendingLivePreviewStateUpdateTimer) {
		clearTimeout(pendingLivePreviewStateUpdateTimer);
		pendingLivePreviewStateUpdateTimer = null;
	}

	const stateUpdates = pendingLivePreviewStateUpdates;
	pendingLivePreviewStateUpdates = [];
	state.set(["appState", "previewChecklist", "stateUpdatesPending"], false);
	if (!stateUpdates.length) {
		return;
	}

	state.set(["appState", "stateUpdates"], stateUpdates);
	state.commit();
}

function queuePreviewStateUpdates(stateUpdates) {
	stateUpdates.forEach((stateUpdate) => {
		if (isSelectedStateUpdate(stateUpdate)) {
			pendingLivePreviewStateUpdates = pendingLivePreviewStateUpdates.filter((pendingUpdate) => !isSelectedStateUpdate(pendingUpdate));
		}

		const updateKey = getLivePreviewStateUpdateKey(stateUpdate);
		const canCoalesce = stateUpdate && (stateUpdate.transactionType === "set" || stateUpdate.transactionType === "unset");
		const existingIndex = canCoalesce
			? pendingLivePreviewStateUpdates.findIndex((pendingUpdate) => getLivePreviewStateUpdateKey(pendingUpdate) === updateKey)
			: -1;
		if (existingIndex >= 0) {
			pendingLivePreviewStateUpdates[existingIndex] = stateUpdate;
		} else {
			pendingLivePreviewStateUpdates.push(stateUpdate);
		}
	});
	state.set(["appState", "previewChecklist", "stateUpdatesPending"], pendingLivePreviewStateUpdates.length > 0);
}

function scheduleLivePreviewStateUpdates(stateUpdates) {
	queuePreviewStateUpdates(stateUpdates);

	if (pendingLivePreviewStateUpdateTimer) {
		clearTimeout(pendingLivePreviewStateUpdateTimer);
	}
	pendingLivePreviewStateUpdateTimer = setTimeout(flushPendingPreviewStateUpdates, LIVE_PREVIEW_TEXT_UPDATE_DELAY_MS);
}

// JJB: Did see weird issue that if two sets in a row one can overwrite the other, saw this on driveRootPath
state.on("update", function (e) {
	// console.log("*!*!*! WHAT 1?");
	const previewUpdateMode = getPreviewUpdateMode();
	if (previewUpdateMode === PREVIEW_UPDATE_MODE_MANUAL) {
		return;
	}

	let eventData = e.data;

	const firstLevelItemType = getFirstLevelItemType(state.get(["tree", "root"]));

	const stateUpdates = [];

	let hasUpdates = false;
	eventData.transaction.forEach(transaction => {
		if (transaction.path.length === 2 && transaction.path[0] === "appState" && transaction.path[1] === "stateUpdates") {
			return;
		}

		// Ignore deselection churn; navigation is driven by selected=true updates.
		// This avoids race conditions where virtual outline clicks emit selected=false first.
		if (
			transaction.type === "set" &&
			Array.isArray(transaction.path) &&
			transaction.path.length > 0 &&
			transaction.path[transaction.path.length - 1] === "selected" &&
			transaction.value === false
		) {
			return;
		}

		if (isStateUpdateDebugEnabled()) {
			console.log("*!*!*! Transaction details:", transaction);
		}


		if (transaction.hasOwnProperty("path") && transaction.path.length >= 2 && transaction.path[0] === "tree" && transaction.path[1] === "root" && !transaction.path.includes("driveRootPath")) {
			// console.log('*!*!*! Transaction details:', transaction);

			if (isStateUpdateDebugEnabled()) {
				console.log("*!*!*! stateUpdate " + transaction.path + "; " + transaction.value + "; " + firstLevelItemType);
			}

			if (transaction.path.length > 1) {
				hasUpdates = true;
				stateUpdates.push({
					path: transaction.path,
					value: transaction.value,
					transactionType: transaction.type,
					type: "entities",
					firstLevelItemType: firstLevelItemType
				});
			}
		}
	});

	if (hasUpdates) {
		if (previewUpdateMode === PREVIEW_UPDATE_MODE_ON_REFRESH) {
			queuePreviewStateUpdates(stateUpdates);
			if (stateUpdates.some(isSelectedStateUpdate)) {
				state.set(
					["appState", "previewChecklist", "suppressPlayerSelectionSyncUntil"],
					Date.now() + PREVIEW_PLAYER_SELECTION_SYNC_SUPPRESSION_MS
				);
				flushPendingPreviewStateUpdates();
			}
		} else if (stateUpdates.every(isDebounceableLivePreviewStateUpdate)) {
			scheduleLivePreviewStateUpdates(stateUpdates);
		} else {
			flushPendingPreviewStateUpdates();
			state.set(["appState", "stateUpdates"], stateUpdates);
			state.commit();
		}
	}
});

export default state;
